import { useRef, useState } from "react"

import { focusOnMount } from "@coval/ui"
import { Tooltip } from "@coval/ui/tooltip"
import { Pencil } from "lucide-react"

interface EditablePlotTitleProps {
  value: string
  placeholder: string
  editable: boolean
  onChange: (next: string) => void
}

// Click-to-edit title, mirroring the metric viewer header: a display + pencil
// affordance that swaps to an input on click; Enter/blur commit, Escape cancels.
export function EditablePlotTitle({
  value,
  placeholder,
  editable,
  onChange,
}: EditablePlotTitleProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState("")
  // Escape unmounts the input, whose blur would otherwise commit the draft;
  // this marks the edit cancelled so that blur skips the commit.
  const cancelledRef = useRef(false)

  if (!editable) {
    return (
      <span className="text-sm font-medium text-coval-text">
        {value || placeholder}
      </span>
    )
  }

  if (isEditing) {
    const commit = () => {
      if (!cancelledRef.current) {
        onChange(draft.trim())
      }
      cancelledRef.current = false
      setIsEditing(false)
    }
    return (
      <input
        ref={focusOnMount}
        aria-label="Plot title"
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation()
          if (e.key === "Enter") {
            commit()
          } else if (e.key === "Escape") {
            e.preventDefault()
            cancelledRef.current = true
            setIsEditing(false)
          }
        }}
        className="border-b border-coval-border-active bg-transparent text-sm font-medium text-coval-text outline-none placeholder:text-coval-text-muted"
      />
    )
  }

  return (
    <div className="group/title flex items-center gap-1.5">
      <span className="text-sm font-medium text-coval-text">
        {value || placeholder}
      </span>
      <Tooltip content="Edit plot title">
        <button
          type="button"
          aria-label="Edit plot title"
          className="cursor-pointer"
          onClick={(e) => {
            e.stopPropagation()
            setDraft(value)
            setIsEditing(true)
          }}
        >
          <Pencil
            className="h-3.5 w-3.5 text-coval-text-muted opacity-0 transition-opacity group-hover/title:opacity-100"
            aria-hidden="true"
          />
        </button>
      </Tooltip>
    </div>
  )
}
